import React, { useEffect, useState, useMemo } from 'react';
import { ArrowUpRight, Pulse } from '@phosphor-icons/react';

/* ====================================================================
 * Toxicology Watch — public regulatory news strip for tagc.us
 *
 * Pulls aggregated FDA, CPSC, OEHHA, FTC, and Federal Register updates
 * from the backend `/api/public/toxicology-watch` endpoint (cached 1 hr).
 * Editorial layout — restrained, no fear-based language, outbound links.
 * ==================================================================== */

const SLATE = '#0B1426';
const INK = '#1B2538';
const MUTED = '#5B6577';
const CORAL = '#FF4E6A';

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return null;
  }
}

function relativeFromNow(iso) {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    const diffMin = Math.round((Date.now() - then) / 60000);
    if (diffMin < 60) return `Updated ${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `Updated ${diffHr}h ago`;
    return `Updated ${Math.round(diffHr / 24)}d ago`;
  } catch {
    return null;
  }
}

export default function ToxicologyWatch() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${base}/api/public/toxicology-watch`, { credentials: 'omit' })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Pulse the freshness indicator every 60s so visitors see the page is
  // actively maintained even without a hard refresh. Pause the tick when
  // the tab is hidden so background tabs do no work.
  useEffect(() => {
    let id = null;
    const start = () => {
      if (id) return;
      id = setInterval(() => setTick((t) => t + 1), 60000);
    };
    const stop = () => {
      if (!id) return;
      clearInterval(id);
      id = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const freshness = useMemo(
    () => (data ? relativeFromNow(data.last_updated) : null),
    [data, tick] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <section
      id="toxicology-watch"
      className="border-t"
      style={{ borderColor: '#E3D9C0', backgroundColor: '#FBF7EE' }}
      data-testid="toxicology-watch-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-12 mb-12">
          <div className="lg:col-span-4">
            <p
              className="text-[11px] font-mono uppercase tracking-[0.3em] mb-4"
              style={{ color: CORAL }}
            >
              06 &mdash; Toxicology Watch
            </p>
            <h2
              className="font-serif text-4xl lg:text-5xl leading-[1.05] mb-6"
              style={{ color: SLATE }}
            >
              Regulatory intelligence, continuously curated across regulated industries.
            </h2>
            <div
              className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em]"
              style={{ color: MUTED }}
              data-testid="toxicology-watch-freshness"
            >
              <Pulse size={14} weight="duotone" color={CORAL} />
              <span className="relative">
                <span
                  className="absolute -left-3 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: CORAL, animation: 'tagcPulse 2s ease-in-out infinite' }}
                />
                <span className="pl-1">{freshness || 'Live regulatory feed'}</span>
              </span>
            </div>
            <style>{`@keyframes tagcPulse{0%,100%{opacity:.35;transform:translateY(-50%) scale(.9)}50%{opacity:1;transform:translateY(-50%) scale(1.15)}}`}</style>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 lg:pt-3">
            <p className="text-base lg:text-lg leading-relaxed" style={{ color: MUTED }}>
              Aggregated from the public regulatory record &mdash; FDA, CPSC, EPA,
              OEHHA (California Proposition 65), the FTC, MHRA, OPSS (UK), and the
              U.S. Federal Register. Curated to surface the documents that
              meaningfully shape exposure, claim substantiation, and enforcement
              posture across cosmetics, personal care, consumer products, medical
              devices, pharmaceuticals, industrial chemicals, and environmental
              health.
            </p>
            {data?.source_catalogue && (
              <p
                className="mt-5 text-[11px] font-mono uppercase tracking-[0.2em]"
                style={{ color: MUTED }}
                data-testid="toxicology-watch-sources"
              >
                Sources &middot;{' '}
                {data.source_catalogue.map((s) => s.source.replace(/^[^·]+·\s*/, '')).join(' · ')}
              </p>
            )}
          </div>
        </div>

        {/* Items */}
        {!data && !error && (
          <div
            className="text-[12px] font-mono uppercase tracking-[0.2em]"
            style={{ color: MUTED }}
            data-testid="toxicology-watch-loading"
          >
            Loading regulatory record&hellip;
          </div>
        )}
        {error && (
          <div
            className="text-[12px] font-mono uppercase tracking-[0.2em]"
            style={{ color: MUTED }}
            data-testid="toxicology-watch-error"
          >
            Live feed temporarily unavailable. Direct line: regulatory@tagc.us
          </div>
        )}

        {data?.items?.length > 0 && (
          <ul
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10"
            data-testid="toxicology-watch-list"
          >
            {data.items.map((item, idx) => (
              <li
                key={item.link + idx}
                className="border-t pt-6 flex flex-col"
                style={{ borderColor: '#D6CDB8' }}
                data-testid={`toxicology-watch-item-${idx}`}
              >
                <div className="flex items-baseline justify-between gap-3 mb-3">
                  <span
                    className="text-[10px] font-mono uppercase tracking-[0.22em]"
                    style={{ color: CORAL }}
                  >
                    {item.source}
                  </span>
                  <span
                    className="text-[10px] font-mono uppercase tracking-[0.18em] whitespace-nowrap"
                    style={{ color: MUTED }}
                  >
                    {formatDate(item.published_at) || '—'}
                  </span>
                </div>
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-serif text-[18px] leading-snug mb-3 hover:text-[#FF4E6A] transition-colors"
                  style={{ color: SLATE }}
                  data-testid={`toxicology-watch-item-${idx}-link`}
                >
                  {item.title}
                </a>
                {item.excerpt && (
                  <p
                    className="text-[13px] leading-relaxed mb-4 flex-1"
                    style={{ color: MUTED }}
                  >
                    {item.excerpt}
                  </p>
                )}
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-[0.22em] mt-auto hover:text-[#FF4E6A] transition-colors"
                  style={{ color: INK }}
                >
                  Read source <ArrowUpRight size={11} weight="bold" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
